import { Form, Formik, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { message } from 'antd';

import { queryClient } from 'config/query-client';
import { createPaymentMethod, updatePaymentMethod } from 'services/collections';
import useTranslation from 'customHooks/translate';
import { PaymentMethod, PaymentMethodCustomError } from 'types';
import { PAYMENT_METHODS } from 'events/types';

import { Input } from './styled-components/common-components';
import CustomDialog from './CustomDialog';

const defaultValues = {
  name: '',
};

const PaymentMethodsForm = ({
  open,
  handleClose,
  paymentMethodToUpdate,
  onSuccess,
}) => {
  const [nameError, setNameError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const formRef = useRef<FormikProps<PaymentMethod>>(null);
  const translation = useTranslation();

  return (
    <CustomDialog
      open={open}
      buttonTitle={
        !!paymentMethodToUpdate
          ? `${translation.taxTypes.Update}`
          : `${translation.taxTypes.Create}`
      }
      title={translation.paymentMethods.PaymentMethod}
      handleClose={handleClose}
      onSubmit={() => formRef.current?.handleSubmit()}
      isLoading={loading}
    >
      <Formik
        innerRef={formRef}
        initialValues={paymentMethodToUpdate || defaultValues}
        onSubmit={(values, actions) => {
          setLoading(true);
          const method = paymentMethodToUpdate
            ? updatePaymentMethod
            : createPaymentMethod;
          method({
            ...values,
            id: paymentMethodToUpdate ? paymentMethodToUpdate.id : undefined,
          })
            .then(() => {
              queryClient.invalidateQueries({
                queryKey: [PAYMENT_METHODS],
              });
              handleClose();
              onSuccess(paymentMethodToUpdate ? 'updated' : 'added');
            })
            .catch((error: PaymentMethodCustomError) => {
              error.response?.data?.Message &&
                message.error(error.response?.data?.Message);
              const errorMessageName = error.response?.data.errors
                ?.Name?.[0] as string | undefined;
              setNameError(errorMessageName || null);
            })
            .finally(() => {
              actions.resetForm({ values: defaultValues });
              setLoading(false);
            });
        }}
        enableReinitialize
      >
        {(props) => (
          <Form>
            <div className="flex flex-col gap-[12px]">
              <label
                className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
                htmlFor="name"
              >
                {translation.paymentMethods.PaymentMethod} *
              </label>
              <Input
                className="d-block"
                type="text"
                name="name"
                onChange={props.handleChange}
                placeholder={translation.paymentMethods.PaymentMethod}
                value={props.values.name || ''}
                style={{
                  borderColor: nameError ? 'red' : '',
                }}
              />
              {nameError && (
                <span className="text-[12px] text-red-600">{nameError}</span>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </CustomDialog>
  );
};

export default PaymentMethodsForm;
